import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { useContentContext } from "../../../app/providers/contextIndex";
import { editarContenido } from "../../../services/backend/contentBackendService";
import ContentService from "../utils/contentService";
import { normalizeSlug } from "../utils/pageConfig";
import { applySimpleTextMode, createSimpleSection, editorToSections, getStructuredLines, sectionsToEditor } from "../utils/pageEditorTransforms";
import { getCompany } from "../../../config/companyConfig";
import logger from "../../../shared/utils/logger";

const usePageManager = () => {
    const [loading, setLoading] = useState(false);
    const { activeLandingId: currentLandingId } = useOutletContext() || {};
    const { upsertPageConfig, deletePageConfig } = useContentContext();
    const effectiveLandingId = currentLandingId || getCompany().id;
    const [landingId, setLandingId] = useState(effectiveLandingId);
    const [selectedSlug, setSelectedSlug] = useState("");
    const [contentReady, setContentReady] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [pagesSearch, setPagesSearch] = useState("");
    const [slug, setSlug] = useState("");
    const [navLabel, setNavLabel] = useState("");
    const [title, setTitle] = useState("");
    const [footerColumn, setFooterColumn] = useState(0);
    const [isHome, setIsHome] = useState(false);
    const [textOnly, setTextOnly] = useState(false);
    const [autoSlug, setAutoSlug] = useState(true);
    const [templateDialogVisible, setTemplateDialogVisible] = useState(false);
    const [optionsExpanded, setOptionsExpanded] = useState(false);
    const [previewDialogVisible, setPreviewDialogVisible] = useState(false);
    const [newSectionType, setNewSectionType] = useState("encabezado");
    const [sections, setSections] = useState([createSimpleSection("encabezado")]);
    const [editingIndex, setEditingIndex] = useState(null);
    const toast = useRef(null);
    const textEditorsRef = useRef({});

    const footerColumnsCount = useMemo(() => {
        const columns = getCompany().footer?.columns;
        return Array.isArray(columns) && columns.length ? columns.length : 1;
    }, []);

    const footerColumnOptions = useMemo(() => (
        Array.from({ length: footerColumnsCount }, (_, index) => ({
            value: index,
            label: `Columna ${index + 1}`,
        }))
    ), [footerColumnsCount]);

    const pages = useMemo(() => {
        if (!contentReady) return [];
        return ContentService.getPagesForLanding(landingId);
    }, [landingId, contentReady]);

    const filteredPages = useMemo(() => {
        const term = String(pagesSearch || "").trim().toLowerCase();
        if (!term) return pages;
        return pages.filter((page) => {
            const slugText = String(page?.slug || "").toLowerCase();
            const navText = String(page?.navLabel || "").toLowerCase();
            const titleText = String(page?.title || "").toLowerCase();
            return slugText.includes(term) || navText.includes(term) || titleText.includes(term);
        });
    }, [pages, pagesSearch]);

    useEffect(() => { setLandingId(effectiveLandingId); }, [effectiveLandingId]);
    useEffect(() => { ContentService.initAdmin().then(() => setContentReady(true)); }, []);

    useEffect(() => {
        if (!selectedSlug) return;
        const page = pages.find((item) => item.slug === selectedSlug);
        if (!page) { setSelectedSlug(""); return; }
        setSlug(page.slug || "");
        setNavLabel(page.navLabel || "");
        setTitle(page.title || "");
        setFooterColumn(Number.isInteger(page.footerColumn) ? page.footerColumn : 0);
        setIsHome(page.isHome === true);
        setTextOnly(page.textOnly === true);
        setAutoSlug(false);
        const loadedSections = sectionsToEditor(page.sections || []);
        setSections(loadedSections.length ? loadedSections : [createSimpleSection("encabezado")]);
    }, [selectedSlug, pages]);

    const resetForm = useCallback(() => {
        setSelectedSlug(""); setSlug(""); setNavLabel(""); setTitle("");
        setFooterColumn(0); setIsHome(false); setTextOnly(false);
        setAutoSlug(true); setSections([createSimpleSection("encabezado")]);
    }, []);

    const applyTemplate = useCallback((template) => {
        resetForm();
        const templateSections = (template.sections || []).map((s) => createSimpleSection(s.type, s));
        setSections(templateSections.length ? templateSections : [createSimpleSection("encabezado")]);
        setTemplateDialogVisible(false);
        setFormVisible(true);
    }, [resetForm]);

    const updateSection = useCallback((index, patch) => {
        setSections((prev) => prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item)));
    }, []);

    const addStructuredLine = useCallback((index, config) => {
        const section = sections[index] || {};
        const values = (config?.fields || []).map((field) => String(section[`draft_${field.key}`] || "").trim());
        if (!values[0]) {
            toast.current?.show({ severity: "warn", summary: "Campo obligatorio", detail: `El ${config?.fields?.[0]?.label?.toLowerCase() || "título"} es obligatorio.`, life: 3000 });
            return;
        }
        const newLine = values.join(" | ");
        const currentLines = getStructuredLines(section.itemsText);
        let nextItemsText;
        if (editingIndex !== null && editingIndex < currentLines.length) {
            nextItemsText = currentLines.map((line, idx) => (idx === editingIndex ? newLine : line)).join("\n");
        } else {
            nextItemsText = [...currentLines, newLine].join("\n");
        }
        const resetDraft = (config?.fields || []).reduce((acc, field) => {
            acc[`draft_${field.key}`] = field.input === "select" ? (field.options?.[0]?.value || "") : "";
            return acc;
        }, {});
        updateSection(index, { itemsText: nextItemsText, ...resetDraft });
        setEditingIndex(null);
    }, [sections, updateSection, editingIndex]);

    const editStructuredLine = useCallback((sectionIndex, entryIndex, config) => {
        const section = sections[sectionIndex] || {};
        const currentLines = getStructuredLines(section.itemsText);
        const line = currentLines[entryIndex];
        if (!line) return;
        const values = line.split("|").map((part) => part.trim());
        const patch = { [`draft_${config.fields[0].key}`]: values[0] || "" };
        config.fields.slice(1).forEach((field) => {
            patch[`draft_${field.key}`] = values[config.fields.indexOf(field)] || "";
        });
        updateSection(sectionIndex, patch);
        setEditingIndex(entryIndex);
    }, [sections, updateSection]);

    const cancelEdit = useCallback((sectionIndex, config) => {
        const resetDraft = (config?.fields || []).reduce((acc, field) => {
            acc[`draft_${field.key}`] = field.input === "select" ? (field.options?.[0]?.value || "") : "";
            return acc;
        }, {});
        updateSection(sectionIndex, resetDraft);
        setEditingIndex(null);
    }, [updateSection]);

    const removeStructuredLine = useCallback((sectionIndex, rowIndex) => {
        const section = sections[sectionIndex] || {};
        const currentLines = getStructuredLines(section.itemsText);
        const nextItemsText = currentLines.filter((_, idx) => idx !== rowIndex).join("\n");
        updateSection(sectionIndex, { itemsText: nextItemsText });
        if (editingIndex === rowIndex) setEditingIndex(null);
    }, [sections, updateSection, editingIndex]);

    const applySimpleTextFormat = useCallback((index, mode) => {
        const field = textEditorsRef.current[index];
        if (!field) return;
        const currentValue = String(sections[index]?.paragraphsText || "");
        const result = applySimpleTextMode(currentValue, field.selectionStart, field.selectionEnd, mode);
        updateSection(index, { paragraphsText: result.value });
        setTimeout(() => { field.focus(); field.setSelectionRange(result.selectionStart, result.selectionEnd); }, 0);
    }, [sections, updateSection]);

    const removeSection = useCallback((index) => setSections((prev) => prev.filter((_, idx) => idx !== index)), []);
    const setTextEditorRef = useCallback((index, element) => { if (element) textEditorsRef.current[index] = element; }, []);

    const handleDelete = useCallback(async () => {
        setLoading(true);
        try {
            const contenidoId = ContentService.getActiveContenidoId();
            if (!contenidoId) {
                toast.current?.show({ severity: "warn", summary: "Sin contenido activo", detail: "No se encontró contenido para sincronizar.", life: 4000 });
                return;
            }
            const remainingPages = ContentService.getPagesForLanding(landingId).filter((p) => p.slug !== selectedSlug);
            await editarContenido(contenidoId, { pages: remainingPages });
            await ContentService.refreshContenidosCache();
            deletePageConfig(landingId, selectedSlug);
            resetForm(); setFormVisible(false);
            toast.current?.show({ severity: "success", summary: "Eliminada", detail: "Página eliminada y sincronizada.", life: 3000 });
        } catch (error) {
            logger.error("Error al eliminar página:", error);
            toast.current?.show({ severity: "error", summary: "Error de sincronización", detail: "No se pudo eliminar la página. Intenta de nuevo.", life: 4000 });
        } finally { setLoading(false); }
    }, [landingId, selectedSlug, deletePageConfig, resetForm]);

    const handleSave = useCallback(() => {
        if (title.trim() && !/^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ0-9\s]+$/.test(title.trim())) {
            toast.current?.show({ severity: "warn", summary: "Título inválido", detail: "El título solo puede contener letras, números y espacios.", life: 3000 });
            return;
        }
        setPreviewDialogVisible(true);
    }, [title]);

    const executeSave = useCallback(async () => {
        setLoading(true);
        try {
            const resolvedSlug = normalizeSlug(slug || navLabel || title);
            const resolvedNavLabel = String(navLabel || title || resolvedSlug || "").trim();
            const resolvedTitle = String(title || navLabel || "Página").trim();
            const resolvedSections = editorToSections(sections);
            const currentPages = ContentService.getPagesForLanding(landingId);
            const duplicateSlug = currentPages.find((p) => normalizeSlug(p.slug) === resolvedSlug && p.slug !== selectedSlug);
            if (duplicateSlug) {
                toast.current?.show({ severity: "error", summary: "Slug duplicado", detail: `Ya existe una página con la URL "/${resolvedSlug}". Elige otro título o slug.`, life: 4000 });
                setLoading(false); return;
            }
            const saved = upsertPageConfig(landingId, { slug: resolvedSlug, navLabel: resolvedNavLabel, title: resolvedTitle, footerColumn, isHome, textOnly, sections: resolvedSections });
            if (!saved) {
                toast.current?.show({ severity: "error", summary: "Título inválido", detail: "Debes indicar un título de pagina válido.", life: 3000 });
                return;
            }
            setSlug(resolvedSlug); setNavLabel(resolvedNavLabel); setTitle(resolvedTitle); setSelectedSlug(resolvedSlug);
            const contenidoId = ContentService.getActiveContenidoId();
            if (!contenidoId) {
                toast.current?.show({ severity: "warn", summary: "Sin contenido activo", detail: "Guardado local. No se encontró contenido activo para persistir en el servidor.", life: 4000 });
                return;
            }
            const pageEntry = { slug: resolvedSlug, navLabel: resolvedNavLabel, title: resolvedTitle, isHome, textOnly, footerColumn, sections: resolvedSections };
            const existingIndex = currentPages.findIndex((p) => p.slug === selectedSlug);
            const allPages = existingIndex >= 0 ? currentPages.map((p, i) => (i === existingIndex ? pageEntry : p)) : [...currentPages, pageEntry];
            await editarContenido(contenidoId, { pages: allPages });
            await ContentService.refreshContenidosCache();
            toast.current?.show({ severity: "success", summary: "Sincronizado", detail: "Guardado correctamente.", life: 3000 });
        } catch (error) {
            logger.error("Error al sincronizar contenido:", error);
            toast.current?.show({ severity: "error", summary: "Error de sincronización", detail: "La página se guardó localmente pero no se pudo sincronizar con el servidor.", life: 4000 });
        } finally { setLoading(false); }
    }, [slug, navLabel, title, sections, landingId, selectedSlug, footerColumn, isHome, textOnly, upsertPageConfig]);

    return {
        loading, landingId, selectedSlug, setSelectedSlug,
        formVisible, setFormVisible, pagesSearch, setPagesSearch,
        slug, setSlug, navLabel, setNavLabel, title, setTitle,
        footerColumn, setFooterColumn, isHome, setIsHome,
        textOnly, setTextOnly, autoSlug, setAutoSlug,
        templateDialogVisible, setTemplateDialogVisible,
        optionsExpanded, setOptionsExpanded,
        previewDialogVisible, setPreviewDialogVisible,
        newSectionType, setNewSectionType,
        sections, setSections,
        editingIndex,
        toast,
        footerColumnOptions, pages, filteredPages,
        resetForm, applyTemplate, updateSection,
        addStructuredLine, editStructuredLine, cancelEdit, removeStructuredLine,
        applySimpleTextFormat, removeSection, setTextEditorRef,
        handleDelete, handleSave, executeSave,
        upsertPageConfig,
    };
};

export default usePageManager;
